import { Outlet, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Users2, HelpCircle, MessageSquare } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useUnreadMessageCount } from '@/hooks/useUnreadMessageCount';
import { MobileNav } from '@/components/MobileNav';
import { DesktopDrawer } from '@/components/DesktopDrawer';
import { getAdminNavGroups } from '@/config/navigation';
import type { NavGroup } from '@/config/navigation';
import Footer from '@/components/Footer';
import { Header } from '@/components/Header';

/**
 * Support Staff Navigation Groups (Inline)
 *
 * Kept inline (not in config/navigation.ts) because:
 * 1. Minimal complexity (1 group, 1 link)
 * 2. No desktop navigation equivalent
 * 3. Role-specific to Layout component
 */
const baseSupportStaffNavGroups: NavGroup[] = [
  {
    title: 'Navigation',
    icon: MapPin,
    links: [{ to: '/', label: 'Dashboard' }],
  },
  {
    title: 'Messages',
    icon: MessageSquare,
    requiresModule: 'messaging',
    links: [
      { to: '/messages?tab=inbox', label: 'Inbox', requiresModule: 'messaging' },
      { to: '/messages?tab=updates', label: 'Updates', requiresModule: 'messaging' },
    ],
  },
  {
    title: 'Help',
    icon: HelpCircle,
    links: [{ to: '/help', label: 'Help & Support' }],
  },
];

const Layout = () => {
  const { user, logout, tenant, viewMode, setViewMode } = useAuthStore();
  const navigate = useNavigate();
  const { isMobile } = useMediaQuery();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const counts = useUnreadMessageCount();
  const { t } = useTranslation('parent');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Parent navigation groups (translated, built inside component for i18n)
  const parentNavGroups: NavGroup[] = useMemo(() => [
    {
      title: t('nav.navigation'),
      icon: Users2,
      links: [
        { to: '/', label: t('nav.dashboard') },
        { to: '/activity', label: t('nav.activity') },
        { to: '/messages', label: t('nav.messages'), requiresModule: 'messaging' as const },
        { to: '/schedule', label: t('nav.schedule') },
        { to: '/settings', label: t('nav.settings') },
        { to: '/profile', label: t('nav.profile') },
      ],
    },
    {
      title: t('nav.help'),
      icon: HelpCircle,
      links: [{ to: '/help', label: t('nav.helpAndSupport') }],
    },
  ], [t]);

  // Compute dynamic admin navigation groups based on scheduling UI mode and lunch visibility
  // Module-based filtering (attendance, parentPortal, messaging) handled by DesktopDrawer/MobileNav
  const adminNavGroupsDynamic = useMemo(() => {
    const uiMode = tenant?.settings?.schedulingUiMode || 'both';
    const showLunchGroups = tenant?.settings?.showLunchGroups === true;
    return getAdminNavGroups(uiMode, showLunchGroups);
  }, [tenant?.settings?.schedulingUiMode, tenant?.settings?.showLunchGroups]);

  // Inject unread badge into nav groups
  // Uses link.to (route path) for matching instead of link.label to support translated labels
  const navGroupsWithBadges = useMemo(() => {
    const injectBadge = (groups: NavGroup[]): NavGroup[] =>
      groups.map((group) => {
        const isMessagesGroup = group.links.some((l) => l.to === '/inbox' || l.to === '/updates');
        const isParentNav = group.links.some((l) => l.to === '/messages');
        if (!isMessagesGroup && !isParentNav) return group;
        return {
          ...group,
          ...(isMessagesGroup && counts.total > 0 ? { badge: counts.total } : {}),
          links: group.links.map((link) => {
            if (link.to === '/inbox') return { ...link, badge: counts.messageUnread };
            if (link.to === '/updates') return { ...link, badge: counts.announcementUnread };
            if (link.to === '/messages') return { ...link, badge: counts.total };
            return link;
          }),
        };
      });

    // When admin is viewing as another role, show that role's navigation
    if (user?.role === 'admin' && viewMode === 'support_staff') {
      return injectBadge(baseSupportStaffNavGroups);
    }

    switch (user?.role) {
      case 'support_staff':
        return injectBadge(baseSupportStaffNavGroups);
      case 'parent':
        return injectBadge(parentNavGroups);
      case 'admin':
      case 'super_admin':
        return injectBadge(adminNavGroupsDynamic);
      default:
        return injectBadge(adminNavGroupsDynamic);
    }
  }, [user?.role, viewMode, adminNavGroupsDynamic, parentNavGroups, counts]);

  // Outer container: column with header at top, then content row below
  const outerLayoutStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  };

  // Content row: drawer + main content side by side (below header)
  const contentRowStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
  };

  // Main content wrapper: column with main + footer
  const mainContentStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0, // Prevent flex item from overflowing
  };

  const mainStyle: React.CSSProperties = {
    flex: 1,
    padding: '1.5rem 0',
    paddingBottom: `calc(1.5rem + var(--safe-area-inset-bottom))`,
  };

  // Check if user should see navigation (all tenant roles except teacher who has own layout)
  const showNavigation =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'support_staff' || user?.role === 'parent';

  return (
    <div style={outerLayoutStyle}>
      {/* Header spans full width at top */}
      <Header
        tenant={tenant}
        user={user}
        onLogout={handleLogout}
        showHamburger={isMobile && showNavigation}
        onHamburgerClick={() => setMobileMenuOpen(true)}
      />

      {/* Mobile Navigation */}
      {isMobile && showNavigation && (
        <MobileNav
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          groups={navGroupsWithBadges}
          userInfo={
            user
              ? {
                  email: user.email,
                  role: user.role,
                }
              : undefined
          }
          viewMode={viewMode}
          onViewModeSwitch={(mode) => {
            setViewMode(mode);
            navigate('/');
          }}
          onLogout={handleLogout}
        />
      )}

      {/* Content row: drawer + main */}
      <div style={contentRowStyle}>
        {/* Desktop Navigation Drawer */}
        {!isMobile && showNavigation && (
          <DesktopDrawer navGroups={navGroupsWithBadges} user={user} onLogout={handleLogout} />
        )}

        {/* Main Content Area */}
        <div style={mainContentStyle}>
          <main style={mainStyle}>
            <Outlet />
          </main>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Layout;
